import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { GenericButton } from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import {
  GenericTable,
  type GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import {
  SkeletonLine,
  SkeletonTableRows,
} from "@ui-components/Skeleton/GenericSkeleton";
import { ReactComponent as IconEdit } from "@assets/Icons/IconEdit_KE.svg";
import { ReactComponent as IconTrash } from "@assets/Icons/IconDelete_KE.svg";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import {
  buildPredicate,
  type ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import {
  fetchGroups,
  deleteGroup,
} from "@redux/slices/notificationsV2/groupsSlice";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import type { NotificationGroup } from "../types";
import { NOTIFICATION_GROUP_NEW_PATH } from "../GroupForm/GroupFormPage";
import {
  groupsColumns,
  toGroupRow,
  matchesStatusSelection,
  type GroupRow,
} from "../GroupsData";
import { makeGroupsFields } from "../groupsFieldSpec";

type AlertConfig = {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "confirm" | "success" | "error" | "warning";
  hideCancel?: boolean;
  resolve: (value: boolean) => void;
};

type GroupsStats = {
  totalGroups: number;
  totalRecipients: number;
  totalResources: number;
  incomplete: number;
};

/**
 * Shared state for the Notification Groups tab. The parent page
 * (`NotificationsV2Page`) renders the KPI strip inside one card and the
 * table inside another, so the two views need a single source of truth
 * for the searchbar filters, derived rows and load state. We share that
 * via context instead of prop-drilling so each visual section stays
 * self-contained but the search-typing → KPI-refresh loop still works.
 */
interface GroupsTabContextValue {
  rows: GroupRow[];
  filteredData: GroupRow[];
  stats: GroupsStats;
  isGroupsListLoading: boolean;
  /** True after the first successful or failed fetch completes. */
  hasFetched: boolean;
  error: string | null;
  reload: () => void;
  activeFilters: ParsedFilter[];
  setActiveFilters: (filters: ParsedFilter[]) => void;
  suggestedKeys: string[];
}

const GroupsTabContext = createContext<GroupsTabContextValue | null>(null);

export const useGroupsTabContext = (): GroupsTabContextValue => {
  const ctx = useContext(GroupsTabContext);
  if (!ctx) {
    throw new Error(
      "Groups tab subcomponents must be rendered inside <GroupsTabProvider>"
    );
  }
  return ctx;
};

/**
 * Owns the data + filter state for the Notification Groups tab. Mount
 * this above any group-related subcomponent (KPI strip, content card)
 * so they see the same `activeFilters` + derived `filteredData`. Safe
 * to mount once at the page level.
 */
export const GroupsTabProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const api = useAxiosAuth0();
  const tenantId = useAppSelector((s) => s.selection.tenantId);
  const { groups, loading, error } = useAppSelector(
    (s) => s.notificationsV2Groups
  );
  const { statusFilterSelection } = useAppSelector(
    (s) => s.notificationsGroupsFilters
  );

  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const wasLoading = useRef(false);

  useEffect(() => {
    if (loading) {
      wasLoading.current = true;
    } else if (wasLoading.current && !hasFetched) {
      setHasFetched(true);
    }
  }, [loading, hasFetched]);

  const rows = useMemo(() => groups.map(toGroupRow), [groups]);

  const fields = useMemo(() => makeGroupsFields(), []);
  const suggestedKeys = useMemo(() => fields.map((f) => f.label), [fields]);

  const normalized = useMemo(
    () =>
      normalizeFilters<GroupRow>(activeFilters, fields, {
        defaultStringToContains: true,
        quotedStringsExact: true,
      }),
    [activeFilters, fields]
  );

  const searchPredicate = useMemo(
    () => buildPredicate<GroupRow>(normalized, fields),
    [normalized, fields]
  );

  const filteredData = useMemo(
    () =>
      rows.filter(
        (row) =>
          searchPredicate(row) &&
          matchesStatusSelection(row, statusFilterSelection)
      ),
    [rows, searchPredicate, statusFilterSelection]
  );

  const stats = useMemo<GroupsStats>(() => {
    const totalGroups = filteredData.length;
    const totalRecipients = filteredData.reduce(
      (acc, r) => acc + (r.memberCount ?? 0),
      0
    );
    const totalResources = filteredData.reduce(
      (acc, r) => acc + (r.assignmentCount ?? 0),
      0
    );
    const incomplete = filteredData.filter(
      (r) => (r.memberCount ?? 0) === 0 || (r.assignmentCount ?? 0) === 0
    ).length;
    return { totalGroups, totalRecipients, totalResources, incomplete };
  }, [filteredData]);

  const reload = useCallback(() => {
    if (tenantId) dispatch(fetchGroups({ api, tenantId }));
  }, [api, dispatch, tenantId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const value = useMemo<GroupsTabContextValue>(
    () => ({
      rows,
      filteredData,
      stats,
      isGroupsListLoading: loading,
      hasFetched,
      error,
      reload,
      activeFilters,
      setActiveFilters,
      suggestedKeys,
    }),
    [
      rows,
      filteredData,
      stats,
      loading,
      hasFetched,
      error,
      reload,
      activeFilters,
      suggestedKeys,
    ]
  );

  return (
    <GroupsTabContext.Provider value={value}>
      {children}
    </GroupsTabContext.Provider>
  );
};

/**
 * Single stat block rendered inside the unified KPI card. Visual language
 * mirrors the Backbone detail header (`BackboneDetails` Bandwidth /
 * Connectors cards) — secondary-tinted label on top, prominent value
 * below in brand color (or warning / success accent) and an optional
 * secondary hint. No own `GenericCard` wrapper: the parent owns the
 * shell so all four KPIs sit inside the same surface.
 */
const Stat: React.FC<{
  label: string;
  value: number | string;
  accent?: "default" | "warning" | "success";
  hint?: string;
}> = ({ label, value, accent = "default", hint }) => {
  const valueColor =
    accent === "warning"
      ? "text-semantic-text-warning"
      : accent === "success"
      ? "text-semantic-text-success"
      : "text-semantic-text-brand";
  return (
    <div className="flex flex-col min-w-0">
      <span className="text-xs uppercase tracking-wide text-semantic-text-secondary">
        {label}
      </span>
      <span className={`text-2xl font-semibold mt-1 tabular-nums ${valueColor}`}>
        {value}
      </span>
      {hint && (
        <span className="text-xs text-semantic-text-secondary mt-1 truncate">
          {hint}
        </span>
      )}
    </div>
  );
};

/**
 * KPI strip for the Notification Groups tab. Renders only the grid (no
 * card shell) so it can slot into the outer page card that already hosts
 * the tabs. Reads everything from {@link GroupsTabProvider}.
 *
 * `loading` renders a skeleton in place of the four stat blocks — used when
 * switching back to this tab while the groups list silently refetches (the
 * global overlay was removed in favor of scoped skeletons). Only rendered by
 * the parent when there IS data, so a brand-new tenant shows no KPI strip.
 */
export const GroupsKpisSection: React.FC<{ loading?: boolean }> = ({
  loading = false,
}) => {
  const { stats } = useGroupsTabContext();
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex flex-col min-w-0 gap-2">
            <SkeletonLine w="55%" h={12} />
            <SkeletonLine w="40%" h={26} />
            <SkeletonLine w="80%" h={12} />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Stat
        label="Groups"
        value={stats.totalGroups}
        hint="Matching filters"
      />
      <Stat
        label="Recipients"
        value={stats.totalRecipients}
        hint="Across filtered groups"
      />
      <Stat
        label="Resources covered"
        value={stats.totalResources}
        hint="Assignments in filtered groups"
      />
      <Stat
        label="Need attention"
        value={stats.incomplete}
        accent={stats.incomplete > 0 ? "warning" : "success"}
        hint={
          stats.incomplete > 0
            ? "Groups missing recipients or resources"
            : "All filtered groups are complete"
        }
      />
    </div>
  );
};

/**
 * Content card for the Notification Groups tab: searchbar + "New group"
 * button + the groups table (with empty / loading / filtered-empty
 * states). Sits in its own `GenericCard` so the page renders two
 * sibling cards (header with KPIs + this one) without nesting.
 */
export const GroupsContentCard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const api = useAxiosAuth0();
  const tenantId = useAppSelector((s) => s.selection.tenantId);
  const {
    rows,
    filteredData,
    isGroupsListLoading,
    hasFetched,
    error,
    reload,
    setActiveFilters,
    suggestedKeys,
  } = useGroupsTabContext();

  // Busy = the list is (re)fetching or this mount hasn't settled its first
  // fetch yet. `hasFetched` resets whenever the tab remounts (e.g. coming
  // back from My Notifications), so this drives the scoped skeletons that
  // replaced the old global overlay. `rows` survives in Redux across the
  // remount, so a tab switch shows the table skeleton over cached counts.
  const isBusy = !hasFetched || isGroupsListLoading;

  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);
  // Quick-delete mutation flag — drives the standard `useProcessingOverlay`
  // spinner so the user sees feedback between confirming the warning alert
  // and the success/error alert. Same hook the page uses for initial load,
  // matching the "save-time mutations" convention in the V2 skill.
  const [isDeleting, setIsDeleting] = useState(false);
  const deletingOverlay = useProcessingOverlay(isDeleting);

  const showConfirmationAlert = useCallback(
    (config: Omit<AlertConfig, "resolve">): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        setAlertConfig({ ...config, resolve });
      });
    },
    []
  );

  const showAlert = useCallback(
    (config: Omit<AlertConfig, "resolve">): Promise<void> => {
      return new Promise<void>((resolve) => {
        setAlertConfig({
          ...config,
          hideCancel: true,
          resolve: () => resolve(),
        });
      });
    },
    []
  );

  const openCreate = () => {
    navigate(NOTIFICATION_GROUP_NEW_PATH);
  };

  const openEdit = useCallback(
    (g: NotificationGroup) => {
      navigate(`/account/notifications/groups/${g.id}`);
    },
    [navigate]
  );

  const handleQuickDelete = useCallback(
    async (row: GroupRow) => {
      if (!tenantId) return;
      const confirmed = await showConfirmationAlert({
        title: `Delete "${row.name}"?`,
        description:
          "This action cannot be undone. The group and all its recipient and resource assignments will be removed.",
        confirmLabel: "Yes, delete it",
        cancelLabel: "Cancel",
        variant: "error",
      });
      if (!confirmed) return;
      setIsDeleting(true);
      let succeeded = false;
      try {
        await dispatch(
          deleteGroup({ api, tenantId, groupId: row.id })
        ).unwrap();
        succeeded = true;
      } catch {
        succeeded = false;
      }
      // Tear the spinner down BEFORE surfacing the result alert — the
      // overlay sits at z-[9999] so leaving it up would render on top of
      // the success/error alert and feel like the action is still
      // running.
      setIsDeleting(false);
      if (succeeded) {
        await showAlert({
          title: "Group deleted",
          description: `"${row.name}" was removed.`,
          confirmLabel: "OK",
          variant: "success",
        });
      } else {
        await showAlert({
          title: "Error",
          description: "Couldn't delete the group. Please try again.",
          confirmLabel: "OK",
          variant: "error",
        });
      }
    },
    [api, dispatch, showAlert, showConfirmationAlert, tenantId]
  );

  const columns = useMemo<GenericTableColumn<GroupRow>[]>(() => {
    const cols: GenericTableColumn<GroupRow>[] = groupsColumns.map((col) => {
      if (col.header === "Group") {
        return {
          ...col,
          renderCell: (row: GroupRow) => (
            <button
              type="button"
              onClick={() => openEdit(row)}
              className="flex items-center gap-2 min-w-0 hover:text-semantic-text-brand hover:underline focus:underline active:underline font-medium text-semantic-text-brand text-left"
            >
              <span
                aria-hidden
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{
                  background: row.color ?? "var(--color-primitive-gray-300)",
                }}
              />
              <span className="truncate">{row.name}</span>
            </button>
          ),
        };
      }
      return col;
    });

    cols.push({
      header: "Actions",
      renderCell: (row: GroupRow) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openEdit(row);
            }}
            className="flex items-center justify-center border border-semantic-border-secondary rounded text-semantic-text-secondary hover:bg-semantic-state-active w-8 h-8"
            aria-label={`Edit ${row.name}`}
            title="Edit"
          >
            <IconEdit className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void handleQuickDelete(row);
            }}
            className="flex items-center justify-center border border-semantic-border-secondary rounded text-semantic-text-secondary hover:bg-semantic-state-active hover:text-semantic-text-error w-8 h-8"
            aria-label={`Delete ${row.name}`}
            title="Delete"
          >
            <IconTrash className="w-4 h-4" />
          </button>
        </div>
      ),
      width: "120px",
      fixed: "right",
    });

    return cols;
  }, [openEdit, handleQuickDelete]);

  if (error) {
    return (
      <>
        <div className="space-y-4">
          <ErrorFetchingItems text={error} />
          <div className="flex justify-center">
            <GenericButton label="Retry" variant="secondary" size="md" onClick={reload} />
          </div>
        </div>
        {alertConfig && (
          <GenericAlert
            isOpen
            onConfirm={() => { alertConfig.resolve(true); setAlertConfig(null); }}
            onClose={() => { alertConfig.resolve(false); setAlertConfig(null); }}
            title={alertConfig.title}
            description={alertConfig.description}
            confirmLabel={alertConfig.confirmLabel}
            cancelLabel={alertConfig.cancelLabel}
            variant={alertConfig.variant}
            hideCancel={alertConfig.hideCancel}
          />
        )}
      </>
    );
  }

  return (
    <>
      <GenericCard
        isCollapsible={false}
        className="flex flex-col"
        bodyContent={
          isBusy ? (
            // Loading (first load OR silent refetch, e.g. coming back to this
            // tab): show ONLY the content skeleton — no searchbar, no "New
            // group" button — so the loading state reads as a single clean
            // placeholder. The KPI strip skeleton is rendered by the page.
            <SkeletonTableRows
              rows={8}
              cols={1}
              rowHeight={14}
              rowGapClassName="space-y-3"
            />
          ) : rows.length === 0 ? (
            // True empty state (settled, no groups for this tenant) — render
            // the message INSIDE this card, where the table normally sits,
            // using the compact `inline` variant. The module tabs (incl. `My
            // Notifications`) stay visible because `NotificationsV2Page` no
            // longer collapses the whole view on empty. The "New group" CTA
            // comes from EmptyItems.
            <EmptyItems inline actionLabel="New group" onAction={openCreate} />
          ) : (
            <div className="flex flex-col">
              <div className="mb-4 bg-[var(--semantic-bg-primary)] flex-shrink-0">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between my-3 px-1 sm:px-0">
                  <div className="w-full max-w-full sm:max-w-[500px] min-w-0">
                    <GenericSearchbar
                      placeholder="Find by attribute or value"
                      suggestedKeys={suggestedKeys}
                      onFiltersChange={setActiveFilters}
                    />
                  </div>
                  <div className="flex justify-end shrink-0 sm:pl-4">
                    <GenericButton
                      label="New group"
                      variant="brand"
                      size="md"
                      onClick={openCreate}
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1 min-h-0 flex flex-col">
                {filteredData.length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-semantic-border-secondary rounded-md">
                    <p className="text-sm text-semantic-text-secondary">
                      No groups match your filters.
                    </p>
                  </div>
                ) : (
                  <GenericTable<GroupRow>
                    columns={columns}
                    data={filteredData}
                    initialRowsPerPage={10}
                    rowsPerPageOptions={[10, 20, 30]}
                    width="100%"
                    tableClassName="bg-semantic-bg-primary"
                    maxTableBodyHeight="100%"
                  />
                )}
              </div>
            </div>
          )
        }
      />

      {alertConfig && (
        <GenericAlert
          isOpen
          onConfirm={() => {
            alertConfig.resolve(true);
            setAlertConfig(null);
          }}
          onClose={() => {
            alertConfig.resolve(false);
            setAlertConfig(null);
          }}
          title={alertConfig.title}
          description={alertConfig.description}
          confirmLabel={alertConfig.confirmLabel}
          cancelLabel={alertConfig.cancelLabel}
          variant={alertConfig.variant}
          hideCancel={alertConfig.hideCancel}
        />
      )}

      {deletingOverlay}
    </>
  );
};
